import { useContext, useState } from 'react';
import { AppContext } from '@/context/AppContext';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import EmptyState from '@/components/shared/EmptyState';
import Modal from '@/components/shared/Modal';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import ClientForm from './ClientForm';
import { formatDate, formatDomain } from '@/utils/formatUtils';
import * as api from '@/services/api';

export default function ClientManager() {
  const { clients, loadClients, switchClient, activeClientId, addToast } = useContext(AppContext);
  const [showForm, setShowForm] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [deleteClient, setDeleteClient] = useState(null);

  const handleDelete = async () => {
    if (!deleteClient) return;
    const { error } = await api.deleteClient(deleteClient.id);
    if (error) {
      addToast('Failed to delete client: ' + error, 'error');
    } else {
      addToast('Client deleted', 'success');
      await loadClients();
      if (deleteClient.id === activeClientId && clients.length > 1) {
        const remaining = clients.filter(c => c.id !== deleteClient.id);
        if (remaining.length > 0) switchClient(remaining[0].id);
      }
    }
    setDeleteClient(null);
  };

  return (
    <div data-testid="client-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div className="section-label flex-1">
          <span>Clients</span>
          <span className="hidden sm:block flex-1 h-px bg-[#1E2D45]" />
        </div>
        <button
          onClick={() => { setEditClient(null); setShowForm(true); }}
          className="button-primary text-xs sm:text-sm flex items-center justify-center gap-1.5 sm:gap-2 w-full sm:w-auto"
          data-testid="add-client-btn"
        >
          <Plus size={14} className="sm:w-4 sm:h-4" />
          Add Client
        </button>
      </div>

      {clients.length === 0 ? (
        <EmptyState
          icon="🏢"
          title="No Clients Yet"
          message="Add your first client to start tracking their SEO rankings."
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {clients.map(client => (
            <div
              key={client.id}
              className={`card cursor-pointer transition-all ${
                client.id === activeClientId ? 'border-[#0070F3]' : ''
              }`}
              onClick={() => switchClient(client.id)}
              data-testid={`client-card-${client.id}`}
            >
              <div className="flex items-start justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-white font-bold font-syne flex-shrink-0"
                    style={{ backgroundColor: client.brand_color || '#1A2338' }}
                  >
                    {client.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold font-syne text-[#E2E8F0] truncate">{client.name}</h3>
                    <span className="text-[9px] sm:text-[10px] font-mono text-[#4A5568] block truncate">
                      {formatDomain(client.domain)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditClient(client); setShowForm(true); }}
                    className="p-1.5 sm:p-2 text-[#4A5568] hover:text-[#E2E8F0] transition-colors"
                    data-testid={`edit-client-${client.id}`}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteClient(client); }}
                    className="p-1.5 sm:p-2 text-[#4A5568] hover:text-[#EF4444] transition-colors"
                    data-testid={`delete-client-${client.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-[9px] sm:text-[10px] font-mono text-[#4A5568]">
                <span className="uppercase">{client.scan_freq} scans</span>
                <span>Added {formatDate(client.created_at)}</span>
              </div>

              {client.id === activeClientId && (
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-[#1E2D45]">
                  <span className="text-[9px] sm:text-[10px] font-mono text-[#00C48C] uppercase">Active</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <Modal
          title={editClient ? 'Edit Client' : 'Add Client'}
          onClose={() => { setShowForm(false); setEditClient(null); }}
        >
          <ClientForm
            client={editClient}
            onClose={() => { setShowForm(false); setEditClient(null); }}
          />
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteClient && (
        <ConfirmDialog
          message={`Are you sure you want to delete "${deleteClient.name}"? This will also delete all associated keywords and rankings.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteClient(null)}
        />
      )}
    </div>
  );
}
